import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, take } from "rxjs/operators";
import { ICachedItem, SecureStorageService, StoragePrefixType } from "../secure-storage/secure-storage-service";
import { NetworkService } from "../network-service/network-service";
import { Injectable, NgZone } from "@angular/core";
import { PracticeDataSyncService } from "src/app/api/proxy/practice/services/practice-data-sync.service";
import { DataSyncTypeType } from "src/app/api/proxy/practice/practice-models";
import { App } from "@capacitor/app";
import { UserInfoService } from "../auth-service/user-info-service";
import { Events } from "../events/events";

@Injectable({
  providedIn: 'root',
})
export class OfflineSyncService {

    private syncTasks: SyncTask<any, any>[] = [];
    private lastEmittedStatusByTask = new WeakMap<SyncTask<unknown, unknown>, TaskStatus>();
    private userUpdatedSub: Subscription | null = null;

    constructor(private _networkService: NetworkService, private _practiceDataSyncService: PracticeDataSyncService) {
        // Web
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.haltAllTasks();
            }
            else if (document.visibilityState === 'visible') {
                this.resumeAllTasks();
            }
        });

        // Ionic / Android
        if ((window as any).Capacitor) {
            App.addListener('appStateChange', ({ isActive }) => {
                if (!isActive) {
                    this.haltAllTasks();
                }
                else{
                    this.resumeAllTasks();
                }
            });
        }

        // Halt UnsyncedEncounter processing while logged out; resume after login.
        this.userUpdatedSub = Events.userUpdated.subscribe((user) => {
            if (!user) {
                this.haltAllTasks();
            } else {
                this.resumeAllTasks();
            }
        });
     }

     // Register a sync task
    registerSyncTask<TRequest, TResponse>(task: SyncTask<TRequest, TResponse>): Subject<TaskStatus> {

        task.InvocationSubject$.subscribe(async (invokedTask) => {{
            await this.executeSyncTask(invokedTask);
        }});

        this.syncTasks.push(task);

        return task.Status;
    }

    removeSyncTask<TRequest, TResponse>(task: SyncTask<TRequest, TResponse>): void {
        task.destroy();
        this.lastEmittedStatusByTask.delete(task as SyncTask<unknown, unknown>);
        this.syncTasks = this.syncTasks.filter(t => t !== task);
    }

    destroyTasks(): void {
        this.syncTasks.forEach(task => task.destroy());
        this.syncTasks = [];
        this.lastEmittedStatusByTask = new WeakMap<SyncTask<unknown, unknown>, TaskStatus>();
        this.userUpdatedSub?.unsubscribe();
        this.userUpdatedSub = null;
    }

    haltAllTasks(): void {
        this.syncTasks.forEach(task => task.HaltProcessing());
    }

    resumeAllTasks(): void {
        this.syncTasks.forEach(task => task.ResumeProcessing());
    }

    private emitStatusIfChanged<TRequest, TResponse>(task: SyncTask<TRequest, TResponse>, status: TaskStatus): void {
        const last = this.lastEmittedStatusByTask.get(task as SyncTask<unknown, unknown>);
        if (last?.RemainingItems === status.RemainingItems && last?.Status === status.Status) {
            return;
        }
        this.lastEmittedStatusByTask.set(task as SyncTask<unknown, unknown>, status);
        task.Status.next(status);
    }

    private async isAuthenticatedForEncounterSync(): Promise<boolean> {
        const user = await UserInfoService.getUserInfo();
        return !!user;
    }

    private isUnauthorizedError(error: any): boolean {
        const status = error?.status ?? error?.statusCode ?? error?.error?.status;
        if (status === 401 || status === 403) {
            return true;
        }
        const message = typeof error?.message === 'string' ? error.message : '';
        return /\b401\b|\b403\b|unauthorized|unauthenticated/i.test(message);
    }

    private async executeSyncTask<TRequest, TResponse>(task: SyncTask<TRequest, TResponse>): Promise<void> {

        // Do not attempt UnsyncedEncounter flush (or burn retries) while logged out.
        if (task.CachePrefixType === StoragePrefixType.UnsyncedEncounter) {
            const authenticated = await this.isAuthenticatedForEncounterSync();
            if (!authenticated) {
                const pending = await SecureStorageService.getItemsByKey<TRequest>(
                    task.CachePrefixType,
                    task.CacheKey,
                );
                this.emitStatusIfChanged(
                    task,
                    new TaskStatus(pending.length, TaskStatusType.Pending),
                );
                return;
            }
        }

        const itemsToSync = await this.handleTaskUpdate(task);

        for (const currentItem of itemsToSync) {
            if (!this._networkService.isOnline) {
                break;
            }

            if (task.CachePrefixType === StoragePrefixType.UnsyncedEncounter) {
                const authenticated = await this.isAuthenticatedForEncounterSync();
                if (!authenticated) {
                    this.emitStatusIfChanged(
                        task,
                        new TaskStatus(itemsToSync.length, TaskStatusType.Pending),
                    );
                    break;
                }
            }

            const abortController = new AbortController();
            const offlineSub = this._networkService.isOnline$
                .pipe(
                    debounceTime(1000),
                    distinctUntilChanged(),
                    filter(online => !online)
                )
                .subscribe(() => {
                    abortController.abort();
                });

            try {
                await task.SyncFunction(currentItem.data, abortController.signal);
                await SecureStorageService.remove(currentItem.id);
                await this.handleTaskUpdate(task);
            } catch (error: any) {
                if (error?.name !== 'AbortError') {
                    console.error(`Error syncing item with id ${currentItem.id}:`, error);
                    await this.HandleFailedItem(task, currentItem, error);
                }
            } finally {
                offlineSub.unsubscribe();
            }
        }
    }

    private async handleTaskUpdate<TRequest>(task: SyncTask<TRequest, any>): Promise<ICachedItem<TRequest>[]> {

        const items = await SecureStorageService.getItemsByKey<TRequest>(task.CachePrefixType, task.CacheKey);
        
        if (items.length === 0) {
            this.emitStatusIfChanged(task, new TaskStatus(0, TaskStatusType.Completed));
            return [];
        }

        if (!this._networkService.isOnline) {
            this.emitStatusIfChanged(task, new TaskStatus(items.length, TaskStatusType.Pending));
            return items;
        }

        this.emitStatusIfChanged(task, new TaskStatus(items.length, TaskStatusType.InProgress));
        return items;
    }

    private async HandleFailedItem<TRequest>(task: SyncTask<TRequest, any>, itemToSync: ICachedItem<TRequest>, error: any): Promise<void> {

        // Keep retry count intact for auth failures — flush again after re-login.
        if (this.isUnauthorizedError(error)) {
            return;
        }

        const retryCount = itemToSync.SyncFunctionRetryCount ?? task.SyncFunctionRetryCount ?? 0;

        if (retryCount > 0) {
            const newCount = retryCount - 1;
            await SecureStorageService.setByFullKey(itemToSync.id, {
                id: itemToSync.id,
                data: itemToSync.data,
                SyncFunctionRetryCount: newCount,
            });
            return;
        }

        const requestType: DataSyncTypeType | undefined = this.mapStoragePrefixToDataSyncType(task.CachePrefixType);
        if (!requestType) {
            console.error(`No mapping for StoragePrefixType ${task.CachePrefixType} to DataSyncTypeType`);
            return;
        }

        this._practiceDataSyncService.dataSyncLogFailedSyncTaskPost({
            body: {
                dataSyncType: requestType,
                exceptionMessage: JSON.stringify(error),
                json: JSON.stringify(itemToSync.data),
            }
        }).subscribe({
            next: async () => {
                await SecureStorageService.remove(itemToSync.id);
            }, 
            error: async () => {
                await SecureStorageService.saveWithPrefix<string>(
                    `${task.CacheKey}_${new Date().toISOString()}`,
                    JSON.stringify(itemToSync.data),
                    task.ArchivePrefix
                );
                await SecureStorageService.remove(itemToSync.id);
            }
        });
    }

    mapStoragePrefixToDataSyncType(CachePrefixType: StoragePrefixType): DataSyncTypeType | undefined {
        switch(CachePrefixType) {
            case StoragePrefixType.Encounter:
            case StoragePrefixType.UnsyncedEncounter:
                return DataSyncTypeType.Encounter;
            case StoragePrefixType.Patient:
                return DataSyncTypeType.Patient;
            case StoragePrefixType.Protocol:
                return DataSyncTypeType.Protocol;
            default:
                return undefined;
        }
    }
}

export interface SyncTaskOptions {
    cacheKey?: string;
    ngZone?: NgZone;
    archivePrefix?: StoragePrefixType;
}

export class SyncTask<TRequest, TResponse>{

    constructor(
        prefixType: StoragePrefixType,
        intervalms: number,
        syncFunction: (item: TRequest, signal?: AbortSignal) => Promise<TResponse>,
        options: SyncTaskOptions
    ) {
        this.CachePrefixType = prefixType;
        this.Intervalms = intervalms;
        this.SyncFunction = syncFunction;
        this.CacheKey = options.cacheKey ?? '';
        this.ArchivePrefix = options.archivePrefix ?? StoragePrefixType.ArchivedData;
        this.Status = new Subject<TaskStatus>();
        this.Options = options;
        this.ResumeProcessing();
    }

    InvocationSubject$ = new Subject<SyncTask<TRequest, TResponse>>();
    Options: SyncTaskOptions;
    CachePrefixType: StoragePrefixType;
    CacheKey: string = '';
    SyncFunctionRetryCount: number = 3;
    ArchivePrefix: StoragePrefixType;
    Intervalms: number;
    SyncFunction: (item: TRequest, signal?: AbortSignal) => Promise<TResponse>; 
    Status: Subject<TaskStatus>;
    private interval: any;

    /** Stops the interval temporarily. Use resumeProcessing() to start again. */
    HaltProcessing(): void {
        clearInterval(this.interval);
        this.interval = undefined;
        // Do not complete InvocationSubject$ — we need it to emit again when resumed
    }

    /** Permanently stops the task and completes the Subject. Use for removal only. */
    destroy(): void {
        clearInterval(this.interval);
        this.interval = undefined;
        this.InvocationSubject$.complete();
    }

    ResumeProcessing(intervalms?: number): void {
        clearInterval(this.interval);
        this.interval = undefined;
        if (intervalms) {
            this.Intervalms = intervalms;
        }

        const tick = () => this.InvocationSubject$.next(this);
        if (this.Options.ngZone) {
            this.Options.ngZone.runOutsideAngular(() => {
                this.interval = setInterval(tick, this.Intervalms);
            });
        } else {
            this.interval = setInterval(tick, this.Intervalms);
        }
    }
}

export class TaskStatus{

    constructor(remainingItems: number = 0, status: TaskStatusType = TaskStatusType.Pending){
        this.RemainingItems = remainingItems;
        this.Status = status;
    }

    RemainingItems: number = 0;
    Status: TaskStatusType = TaskStatusType.Pending;
}

export enum TaskStatusType{
    Pending = 'Pending',
    InProgress = 'InProgress',
    Completed = 'Completed',
}
